/**
 * @file: voice.service.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 */

import { HttpClient } from '@euler/lib/request';
import { Platform } from 'react-native';

export type VoiceAudioFormat =
  | 'pcm'
  | 'wav'
  | 'mp3'
  | 'opus'
  | 'speex'
  | 'amr'
  | 'aac';

export type VoiceSampleRate = 8000 | 16000;

export type VoiceRecognizeOptions = {
  /** Insert punctuation marks into the recognized text. Defaults to `true`. */
  enablePunctuationPrediction?: boolean;
  /**
   * Convert spoken numbers/dates/etc into Arabic numerals (e.g. "一百二" →
   * "120"). Defaults to `true`.
   */
  enableInverseTextNormalization?: boolean;
  /**
   * Enable voice activity detection so silent leading/trailing audio is
   * stripped before recognition. Defaults to `true`.
   */
  enableVoiceDetection?: boolean;
};

export type VoiceRecognizeRequest = VoiceRecognizeOptions & {
  uri: string;
  format?: VoiceAudioFormat;
  sampleRate?: VoiceSampleRate;
  filename?: string;
};

export type VoiceRecognizeResult = {
  text: string;
  taskId: string;
};

const FORMAT_TO_MIME: Record<VoiceAudioFormat, string> = {
  pcm: 'audio/pcm',
  wav: 'audio/wav',
  mp3: 'audio/mpeg',
  opus: 'audio/opus',
  speex: 'audio/speex',
  amr: 'audio/amr',
  aac: 'audio/aac',
};

export class VoiceService {
  constructor(private readonly api: HttpClient) {}

  async recognize(
    request: VoiceRecognizeRequest,
  ): Promise<VoiceRecognizeResult> {
    const formData = await this.buildFormData(request);
    return await this.api
      .post()
      .url('/voice/recognize')
      .data(formData, false)
      .future();
  }

  private async buildFormData(request: VoiceRecognizeRequest) {
    const formData = new FormData();
    const format = request.format ?? inferFormat(request.uri, request.filename);
    const filename = request.filename ?? `audio.${format ?? 'bin'}`;
    const contentType = format
      ? FORMAT_TO_MIME[format]
      : 'application/octet-stream';

    // Append text fields *before* the audio so backend multipart parsers
    // (busboy) see them on the field event before they start streaming the
    // file body.
    if (format) {
      formData.append('metadata[format]', format);
    }
    if (request.sampleRate) {
      formData.append('metadata[sample_rate]', String(request.sampleRate));
    }
    if (request.enablePunctuationPrediction !== undefined) {
      formData.append(
        'metadata[enable_punctuation_prediction]',
        String(request.enablePunctuationPrediction),
      );
    }
    if (request.enableInverseTextNormalization !== undefined) {
      formData.append(
        'metadata[enable_inverse_text_normalization]',
        String(request.enableInverseTextNormalization),
      );
    }
    if (request.enableVoiceDetection !== undefined) {
      formData.append(
        'metadata[enable_voice_detection]',
        String(request.enableVoiceDetection),
      );
    }

    if (Platform.OS === 'web') {
      const resp = await fetch(request.uri);
      const blob = await resp.blob();
      formData.append('audio', blob, filename);
    } else {
      // https://github.com/facebook/react-native/blob/main/Libraries/Network/FormData.js
      formData.append('audio', {
        uri: request.uri,
        type: contentType,
        name: filename,
      } as any);
    }
    return formData;
  }
}

function inferFormat(
  uri: string,
  filename?: string,
): VoiceAudioFormat | undefined {
  const source = filename ?? uri;
  const match = /\.([a-z0-9]+)(?:\?|#|$)/i.exec(source);
  const ext = match?.[1]?.toLowerCase();
  if (!ext) {
    return undefined;
  }
  if (ext in FORMAT_TO_MIME) {
    return ext as VoiceAudioFormat;
  }
  return undefined;
}
